import { useEffect, useRef, useState, useTransition, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { NavIcon } from "@/components/shell/NavIcon";
import { listOwners, listWarehouses, rememberWarehouse, warehouseKeys, type Owner, type Warehouse, type WarehouseList } from "./warehouse.api";
import { BLOCK_REASONS, BLOCK_REASON_LABEL_KEYS } from "./labels";

// What the three Warehouse screens share (docs/design_handoff_wms/README.md §4): the warehouse
// at the top right, remembered on the account; the owner picker of Artikelen; and the few
// Lucide strokes they draw beside the menu's own.

const GLYPHS = {
  chevronDown: <path d="m6 9 6 6 6-6" />,
  chevronRight: <path d="m9 18 6-6-6-6" />,
  lock: (
    <>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ),
  unlock: (
    <>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  close: <path d="M18 6 6 18M6 6l12 12" />,
  package: (
    <>
      <path d="m7.5 4.27 9 5.15" />
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </>
  ),
  plus: <path d="M5 12h14M12 5v14" />,
} as const;

export type GlyphName = keyof typeof GLYPHS;

export function Glyph({ name, size = 15 }: { name: GlyphName; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      {GLYPHS[name]}
    </svg>
  );
}

// ─── The warehouse at the top right ──────────────────────────────────────────

/**
 * The warehouse a screen shows: the one in the address (a shared link), else the one this
 * person chose last, else the first. Choosing one puts it in the address and on the account.
 */
export function useWarehouseChoice(code: string | undefined, onChange: (code: string) => void) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: warehouseKeys.warehouses, queryFn: listWarehouses, staleTime: 60_000 });
  const warehouses = (query.data?.warehouses ?? []).filter((warehouse) => warehouse.isActive);
  const current =
    warehouses.find((warehouse) => warehouse.code === code) ??
    warehouses.find((warehouse) => warehouse.id === query.data?.preferredId) ??
    warehouses[0];

  function choose(warehouse: Warehouse) {
    onChange(warehouse.code);
    queryClient.setQueryData<WarehouseList>(warehouseKeys.warehouses, (prev) => (prev ? { ...prev, preferredId: warehouse.id } : prev));
    // Remembering it is a convenience: a failure leaves the screen as it is.
    rememberWarehouse(warehouse.id).catch(() => undefined);
  }

  return { warehouses, current, choose, isLoading: query.isLoading, error: query.error };
}

/** "Magazijn: Haps ▾" — a secondary button with the native list under it. */
export function WarehousePicker({
  warehouses,
  current,
  onChoose,
}: {
  warehouses: Warehouse[];
  current: Warehouse | undefined;
  onChoose: (warehouse: Warehouse) => void;
}) {
  const { t } = useTranslation();
  return (
    <label className="wl-btn wl-btn--secondary" style={{ position: "relative" }}>
      <NavIcon name="warehouse" size={15} />
      <span>{t("warehouse.pickerLabel", { name: current?.name ?? "—" })}</span>
      <Glyph name="chevronDown" size={14} />
      <select
        aria-label={t("warehouse.pickerAria")}
        value={current?.id ?? ""}
        onChange={(e) => {
          const next = warehouses.find((warehouse) => warehouse.id === e.target.value);
          if (next) onChoose(next);
        }}
        style={{ position: "absolute", inset: 0, width: "100%", opacity: 0, cursor: "pointer" }}
      >
        {warehouses.map((warehouse) => (
          <option key={warehouse.id} value={warehouse.id}>
            {warehouse.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// ─── The owner ───────────────────────────────────────────────────────────────

function useOutsideClose(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return ref;
}

/**
 * "Klant: Voeding Zuid BV ▾": the owners WLX knows, and any sales relation found by name —
 * a customer becomes an owner with its first article.
 */
export function OwnerPicker({ current, onChoose }: { current: { id: string; name: string } | undefined; onChoose: (owner: Owner) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const ref = useOutsideClose(open, () => setOpen(false));
  useEffect(() => {
    const timer = setTimeout(() => setQ(text.trim()), 250);
    return () => clearTimeout(timer);
  }, [text]);
  const owners = useQuery({
    queryKey: warehouseKeys.owners(q),
    queryFn: () => listOwners(q),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="wl-btn wl-btn--secondary" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen((v) => !v)}>
        <span>{current ? t("warehouse.ownerLabel", { name: current.name }) : t("warehouse.ownerChoose")}</span>
        <Glyph name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="wl-popover" style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", width: "320px" }}>
          <div style={{ padding: "var(--space-2)" }}>
            <input
              className="wl-input"
              autoFocus
              type="search"
              value={text}
              placeholder={t("warehouse.ownerSearch")}
              aria-label={t("warehouse.ownerSearch")}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <div role="listbox" aria-label={t("warehouse.ownerListLabel")} style={{ maxHeight: "320px", overflowY: "auto" }}>
            {(owners.data ?? []).map((owner) => (
              <button
                key={owner.id}
                type="button"
                role="option"
                aria-selected={owner.id === current?.id}
                className="wl-menu__item"
                style={{ width: "100%", justifyContent: "space-between", gap: "var(--space-3)" }}
                onClick={() => {
                  onChoose(owner);
                  setOpen(false);
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>{owner.name}</span>
                <span className="t-caption t-muted" style={{ flexShrink: 0 }}>
                  {owner.articleCount > 0 ? t("warehouse.articleCount", { count: owner.articleCount }) : owner.code}
                </span>
              </button>
            ))}
            {owners.data && owners.data.length === 0 && (
              <div className="t-caption t-muted" style={{ padding: "var(--space-2) var(--space-3)" }}>
                {q ? t("warehouse.ownerNone") : t("warehouse.ownerTypeToFind")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Small pieces ────────────────────────────────────────────────────────────

/** Occupancy as a bar and a percentage (handoff §8: one .wl-meter). */
export function Meter({ value, label }: { value: number; label: string }) {
  const percent = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <span className="wl-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent} aria-label={label}>
      <span className="wl-meter__track">
        <span className="wl-meter__fill" style={{ width: `${percent}%` }} />
      </span>
      <span className="wl-meter__value">{percent}%</span>
    </span>
  );
}

/** A card with a title and a line under it, as the article's cards are. */
export function SectionCard({
  title,
  subtitle,
  actions,
  children,
  bodyStyle,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  bodyStyle?: React.CSSProperties;
}) {
  return (
    <section className="wl-card">
      <div className="wl-card__header" style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-3)" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="wl-card__title">{title}</div>
          {subtitle && <div className="wl-card__subtitle">{subtitle}</div>}
        </div>
        {actions}
      </div>
      <div className="wl-card__body" style={bodyStyle}>
        {children}
      </div>
    </section>
  );
}

/**
 * A modal of the design system, closed with Escape or a click beside it. Drawn on the body: opened
 * from a drawer (Voorraad's pallet), the drawer's transform would otherwise hold it inside.
 */
export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const { t } = useTranslation();
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);
  return createPortal(
    <div
      className="wl-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={title} className={`wl-modal${wide ? " wl-modal--wide" : ""}`}>
        <div className="wl-modal__header">
          <span className="wl-modal__title">{title}</span>
          <button type="button" className="wl-btn wl-btn--ghost wl-btn--icon" onClick={onClose} aria-label={t("common.close")}>
            <Glyph name="close" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** The body of a modal form: fields with room around them, the buttons at the foot. */
export function ModalForm({
  onSubmit,
  error,
  children,
  submitLabel,
  submitClass = "wl-btn--primary",
  isPending,
  onCancel,
  disabled,
}: {
  onSubmit: () => void;
  error: string | null;
  children: ReactNode;
  submitLabel: string;
  submitClass?: string;
  isPending: boolean;
  onCancel: () => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <form
      className="wl-modal__body"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="wl-vstack" style={{ gap: "var(--space-4)", alignItems: "stretch", padding: "var(--space-2) var(--space-5) var(--space-4)" }}>
        {children}
        {error && (
          <div className="wl-alert wl-alert--danger" role="alert">
            <span>{error}</span>
          </div>
        )}
        <div className="wl-hstack" style={{ justifyContent: "flex-end", gap: "var(--space-2)" }}>
          <button type="button" className="wl-btn wl-btn--secondary" onClick={onCancel}>
            {t("common.cancel")}
          </button>
          <button type="submit" className={`wl-btn ${submitClass}`} disabled={isPending || disabled}>
            {isPending ? t("common.saving") : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

// ─── Blocking ────────────────────────────────────────────────────────────────

export type BlockTarget = "places" | "pallets";

// The words of the modal for what is blocked: storage locations or pallets.
const BLOCK_WORDS: Record<BlockTarget, Record<"blockTitle" | "releaseTitle" | "blockExplain" | "releaseExplain" | "blocked" | "blockedAlready" | "released", string>> = {
  places: {
    blockTitle: "warehouse.blockTitle",
    releaseTitle: "warehouse.releaseTitle",
    blockExplain: "warehouse.blockExplain",
    releaseExplain: "warehouse.releaseExplain",
    blocked: "warehouse.blockedResult",
    blockedAlready: "warehouse.blockedResultAlready",
    released: "warehouse.releasedResult",
  },
  pallets: {
    blockTitle: "warehouse.stock.blockTitle",
    releaseTitle: "warehouse.stock.releaseTitle",
    blockExplain: "warehouse.stock.blockExplain",
    releaseExplain: "warehouse.stock.releaseExplain",
    blocked: "warehouse.stock.blockedResult",
    blockedAlready: "warehouse.stock.blockedResultAlready",
    released: "warehouse.stock.releasedResult",
  },
};

/** Blokkeren… with a reason, or Vrijgeven with why it may be used again: places and pallets alike. */
export function BlockModal({
  target,
  mode,
  ids,
  block,
  release,
  onClose,
  onDone,
}: {
  target: BlockTarget;
  mode: "block" | "release";
  ids: string[];
  block: (ids: string[], reason: string, remark: string) => Promise<{ blocked: number; alreadyBlocked: number }>;
  release: (ids: string[], remark: string) => Promise<{ released: number }>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const words = BLOCK_WORDS[target];
  const [reason, setReason] = useState<string>("damaged");
  const [remark, setRemark] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const remarkRequired = mode === "release" || reason === "other";

  function submit() {
    setError(null);
    startTransition(async () => {
      try {
        if (mode === "block") {
          const result = await block(ids, reason, remark.trim());
          onDone(
            result.alreadyBlocked > 0
              ? t(words.blockedAlready as never, { count: result.blocked, already: result.alreadyBlocked })
              : t(words.blocked as never, { count: result.blocked }),
          );
        } else {
          const result = await release(ids, remark.trim());
          onDone(t(words.released as never, { count: result.released }));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  return (
    <Modal title={t((mode === "block" ? words.blockTitle : words.releaseTitle) as never, { count: ids.length })} onClose={onClose}>
      <ModalForm
        onSubmit={submit}
        onCancel={onClose}
        error={error}
        isPending={isPending}
        submitLabel={t(mode === "block" ? "warehouse.blockSubmit" : "warehouse.releaseSubmit")}
        disabled={remarkRequired && remark.trim() === ""}
      >
        <p className="t-body-sm" style={{ margin: 0, color: "var(--text-secondary)" }}>
          {t((mode === "block" ? words.blockExplain : words.releaseExplain) as never)}
        </p>
        {mode === "block" && (
          <div className="wl-field">
            <label className="wl-field__label" htmlFor="block-reason">
              {t("warehouse.blockReasonLabel")}
            </label>
            <select id="block-reason" className="wl-select" value={reason} onChange={(e) => setReason(e.target.value)}>
              {BLOCK_REASONS.map((value) => (
                <option key={value} value={value}>
                  {t(BLOCK_REASON_LABEL_KEYS[value] as never)}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="block-remark">
            {t(mode === "block" ? "warehouse.blockRemarkLabel" : "warehouse.releaseRemarkLabel")}
            {!remarkRequired && <span className="t-muted"> · {t("warehouse.optional")}</span>}
          </label>
          <textarea
            id="block-remark"
            className="wl-textarea"
            rows={3}
            maxLength={500}
            value={remark}
            autoFocus={mode === "release"}
            onChange={(e) => setRemark(e.target.value)}
          />
        </div>
      </ModalForm>
    </Modal>
  );
}

/** A number input's value, or null when it is empty; comma or dot. */
export function numberOrNull(text: string): number | null {
  const value = Number(text.trim().replace(",", "."));
  return text.trim() === "" || !Number.isFinite(value) ? null : value;
}

export const NUMBER = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 3 });
